import React, { useEffect, useState } from 'react';
import axios from 'axios';

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exp;
  return `${value >= 10 || exp === 0 ? Math.round(value) : value.toFixed(1)} ${units[exp]}`;
};

const ago = (ms) => {
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)} min`;
  return `${(ms / 3_600_000).toFixed(1)} h`;
};

/**
 * What the live relay is doing right now: whether its browser is up, and
 * each stream it is carrying. Lives on the Data page because that is where
 * the app's other moving parts are shown, and the relay is the one most
 * worth a glance when a match will not start.
 */
const LiveRelayPanel = () => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      axios
        .get('/api/live/status')
        .then(({ data }) => !cancelled && setStatus(data.data))
        .catch(() => !cancelled && setStatus(null));
    load();
    const timer = setInterval(load, 10_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  if (!status) return null;
  const { relay, aggregator } = status;
  const { browser } = relay;

  return (
    <section className="section">
      <div className="section-head">
        <h3 className="section-title">Live relay</h3>
        <span className="section-note">
          {browser.executable ? browser.executable.split(/[\\/]/).pop() : 'no browser found'}
          {' · '}
          {browser.running ? `running, pid ${browser.pid}` : 'idle'}
          {' · streams from '}
          {aggregator.base.replace(/^https?:\/\//, '')}
        </span>
      </div>

      {!browser.executable ? (
        <div className="notice">
          <b>No Chrome or Edge on this machine</b>
          <p>
            Live sport falls back to the mirrors' own embeds, ads included. Install either, or point
            <code> LIVE_BROWSER_PATH </code>
            at one, and restart.
          </p>
        </div>
      ) : null}

      {browser.lastError ? <div className="alert">{browser.lastError}</div> : null}

      {(status.watches || []).length ? (
        <div className="facts" style={{ marginTop: 0, marginBottom: 14 }}>
          {status.watches.map((entry) => (
            <div key={entry.id} className="disk-row">
              <div className="fact-v disk-name">
                {entry.title}
                <span className="relay-meta">
                  {entry.active ? `playing ${entry.active.mirror.language || entry.active.key} (${entry.active.health.state})` : 'nothing playing'}
                  {entry.standby ? ` · standby ${entry.standby.mirror.language || entry.standby.key} (${entry.standby.health.state})` : ' · no standby'}
                  {` · ${entry.mirrors.length} mirror${entry.mirrors.length === 1 ? '' : 's'} known`}
                  {entry.events.length ? ` · ${entry.events[entry.events.length - 1].text}` : ''}
                </span>
              </div>
              <div className="fact-k disk-size">watch</div>
            </div>
          ))}
        </div>
      ) : null}

      {relay.sessions.length ? (
        <div className="facts" style={{ marginTop: 0 }}>
          {relay.sessions.map((session) => (
            <div key={session.id} className="disk-row">
              <div className="fact-v disk-name">
                {session.label || session.embed}
                <span className="relay-meta">
                  {session.state}
                  {session.playlistHost ? ` · ${session.playlistHost}` : ''}
                  {session.resolveMs ? ` · started in ${(session.resolveMs / 1000).toFixed(1)}s` : ''}
                  {` · ${session.viewers} watching · idle ${ago(session.idleMs)}`}
                </span>
              </div>
              <div className="fact-k disk-size">{formatBytes(session.bytes)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty">Nothing is being relayed right now</div>
      )}
    </section>
  );
};

export default LiveRelayPanel;
